import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    BiCalendar, BiCar, BiCheck, BiCheckCircle, BiCoinStack, BiCopy, BiEnvelope, BiErrorCircle, BiLinkExternal,
    BiInfoCircle, BiLoaderAlt, BiMap, BiPackage, BiPhone, BiPurchaseTag, BiRefresh, BiRightArrowAlt, BiSearch, BiShieldQuarter,
    BiSolidStar, BiStore, BiTimeFive, BiX,
} from "react-icons/bi";
import Handler from "../../../Helpers/Handler";
import { RULES, textError } from "../../../Helpers/bookingRules";
import { currencyFormatter, stripHtml, textCapitalize } from "../../../Helpers/Utils";
import DeliveryLocationPicker from "../../../Components/DeliveryLocationPicker";
import BackButton from "../../../Components/BackButton";
import LiveBadge from "../../../Components/LiveBadge";
import TestModeTag from "../../../Components/TestModeTag";
import usePaymentEvents from "../../../Helpers/usePaymentEvents";
import CarBookingRangePicker from "../../../Components/CarBookingRangePicker";
import { DEFAULT_RULES, createDateRules, formatRangeDate, rulesFromSettings, toDate } from "../../../Helpers/dateRange";
import { listBackPath } from "../../../Helpers/useListParams";

// Pickup/drop-off arrive as ?startDate=&endDate= - ISO strings or epoch ms.
// Both are read; anything else is treated as absent.
const readDateParam = (value) => {
    const fromMs = toDate(value);
    if (fromMs) return fromMs;
    const parsed = value ? new Date(value) : null;
    return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
};
// URL dates go through the rules (clamped into the nearest bookable range,
// never rejected - `adjusted` says whether that moved them); with none, the
// earliest legal pickup and a 24-hour trip.
const initialRange = (dates, startParam, endParam) => {
    if (startParam || endParam) return dates.parseRange(readDateParam(startParam), readDateParam(endParam));
    const start = dates.earliestStart();
    return { ...dates.clampRange({ start, end: new Date(start.getTime() + 24 * 36e5) }), adjusted: false };
};
const formatDuration = (hours) => {
    const total = Math.round(Number(hours) || 0);
    if (total <= 0) return "0 hrs";
    const days = Math.floor(total / 24);
    const rem = total % 24;
    const parts = [];
    if (days) parts.push(`${days} day${days > 1 ? "s" : ""}`);
    if (rem || !days) parts.push(`${rem} hr${rem !== 1 ? "s" : ""}`);
    return parts.join(" ");
};
const fullName = (c) => [c?.firstName, c?.lastName].filter(Boolean).join(" ") || c?.customerId || "";
const initialsOf = (c) => (fullName(c).split(/\s+/).map((w) => w[0]).join("").slice(0, 2) || "?").toUpperCase();
const carName = (d) => [d?.brand?.brand, d?.model?.modelName].filter(Boolean).join(" ") || d?.registrationNo || "";
const scrollToSection = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

/* ---- Small building blocks ---------------------------------------------- */

const Section = ({ id, index, title, hint, done, error, optional, children }) => (
    <section className={`rg-sec${error ? " rg-sec--error" : ""}`} id={id} aria-labelledby={`${id}-title`}>
        <div className="rg-sec-head">
            <span className={`rg-sec-num${done ? " is-done" : ""}`} aria-hidden="true">{done ? <BiCheck /> : index}</span>
            <div className="rg-sec-heading">
                <h2 id={`${id}-title`}>{title}{optional && <span className="rg-optional">Optional</span>}</h2>
                {hint && <p>{hint}</p>}
            </div>
        </div>
        <div className="rg-sec-body">
            {children}
            {error && <p className="rg-field-error" role="alert"><BiErrorCircle aria-hidden="true" />{error}</p>}
        </div>
    </section>
);

const Segmented = ({ name, value, onChange, options }) => (
    <div className="rg-seg" role="radiogroup">
        {options.map((o) => (
            <label key={o.value} className={`rg-seg-opt${value === o.value ? " is-on" : ""}`}>
                <input type="radio" name={name} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
                {o.icon}<span>{o.label}</span>
            </label>
        ))}
    </div>
);

const CarGlyph = () => (
    <svg viewBox="0 0 64 32" className="rg-glyph" aria-hidden="true">
        <path d="M6 22c0-3 2-5 5-5l7-7c1-1 3-2 5-2h16c2 0 4 1 5 2l7 7h2c3 0 5 2 5 5v3H6z" fill="currentColor" opacity=".18" />
        <circle cx="18" cy="25" r="4.5" fill="currentColor" opacity=".45" />
        <circle cx="46" cy="25" r="4.5" fill="currentColor" opacity=".45" />
    </svg>
);

const LedgerRow = ({ label, detail, value, tone }) => (
    <div className={`rg-row${tone ? ` rg-row--${tone}` : ""}`}>
        <dt>{label}{detail && <span>{detail}</span>}</dt>
        <dd>{value}</dd>
    </div>
);

/* ---- Page ------------------------------------------------------------------ */

const BookingRegister = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const prefilledCarId = searchParams.get("carId") || "";
    const preselectedCustomerId = searchParams.get("customerId") || "";

    // ---- Customer ----
    const [customerQuery, setCustomerQuery] = useState("");
    const [customerResults, setCustomerResults] = useState([]);
    const [customerSearching, setCustomerSearching] = useState(false);
    const [customerActive, setCustomerActive] = useState(0);
    const [customer, setCustomer] = useState(null);
    const customerDebounce = useRef(null);
    const customerInputRef = useRef(null);
    // The pickup/drop-off the URL arrived with, read once.
    const urlDates = useRef({ start: searchParams.get("startDate"), end: searchParams.get("endDate") }).current;
    // True once the admin has picked dates by hand, so the real rules loading
    // in afterward only clamp what they chose instead of replacing it.
    const dateTouchedRef = useRef(false);

    // ---- Car ----
    const [car, setCar] = useState(prefilledCarId ? { carId: prefilledCarId } : null);
    const [carDetail, setCarDetail] = useState(null);
    const [carDetailLoading, setCarDetailLoading] = useState(!!prefilledCarId);
    const [carQuery, setCarQuery] = useState("");
    const [activeCars, setActiveCars] = useState([]);
    const [carsLoading, setCarsLoading] = useState(true);
    const [activeImage, setActiveImage] = useState(0);

    // ---- Trip ----
    // Date rules start at the server defaults and are replaced by the real
    // admin-channel ones once /admin/booking/lead-time loads.
    const [dates, setDates] = useState(() => createDateRules(DEFAULT_RULES));
    const [firstRange] = useState(() => initialRange(dates, urlDates.start, urlDates.end));
    const [range, setRange] = useState({ start: firstRange.start, end: firstRange.end });
    const rangeRef = useRef(range);
    rangeRef.current = range;
    const { start: startDate, end: endDate } = range;
    const [adjustedNote, setAdjustedNote] = useState((urlDates.start || urlDates.end) && firstRange.adjusted ? firstRange : null);
    const onRangeChange = (next) => {
        dateTouchedRef.current = true;
        setAdjustedNote(null);
        setRange({ start: next.start, end: next.end });
    };
    const [packageKey, setPackageKey] = useState("");

    // ---- Options ----
    const [protectionTiers, setProtectionTiers] = useState([]);
    const [protectionLoaded, setProtectionLoaded] = useState(false);
    const [protectionId, setProtectionId] = useState("");
    const [couponInput, setCouponInput] = useState("");
    const [couponError, setCouponError] = useState(null);
    const [couponCode, setCouponCode] = useState(""); // the applied one - only this is priced
    const [useCoins, setUseCoins] = useState(false);
    // The customer agreed (in person or on the phone) to get booking updates on WhatsApp. Starts unticked.
    const [whatsappConsent, setWhatsappConsent] = useState(false);

    // ---- Handover (delivery / return) ----
    // Lat/lng come from DeliveryLocationPicker; the distance is its live
    // straight-line estimate. The server re-measures (BookingUtils.resolveVerifiedDistance).
    const [deliveryMode, setDeliveryMode] = useState("self"); // "self" | "deliver"
    const [deliveryAddress, setDeliveryAddress] = useState("");
    const [deliveryDistanceKm, setDeliveryDistanceKm] = useState("");
    const [deliveryLat, setDeliveryLat] = useState("");
    const [deliveryLng, setDeliveryLng] = useState("");
    const [deliveryWithinLimit, setDeliveryWithinLimit] = useState(true);
    const [pickupMode, setPickupMode] = useState("self"); // "self" | "collect"
    const [pickupAddress, setPickupAddress] = useState("");
    const [pickupDistanceKm, setPickupDistanceKm] = useState("");
    const [pickupLat, setPickupLat] = useState("");
    const [pickupLng, setPickupLng] = useState("");
    const [pickupWithinLimit, setPickupWithinLimit] = useState(true);
    const [maxDeliveryKm, setMaxDeliveryKm] = useState(null);

    // ---- Quote / booking ----
    const [quote, setQuote] = useState(null); // { key, data }
    const [quoteError, setQuoteError] = useState(null); // { key, message }
    const [quoting, setQuoting] = useState(false);
    const quoteRequestRef = useRef(0);
    // Admin-only waiver of the "live bookings per customer" limit: { customerId, reason }. It belongs to one customer
    // and travels with both the quote and the booking; the server saves the reason on the booking.
    const [liveOverride, setLiveOverride] = useState(null);
    const [overrideReason, setOverrideReason] = useState("");
    const [requote, setRequote] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    const [registering, setRegistering] = useState(false);
    const [payState, setPayState] = useState("waiting"); // waiting | paid | failed | expired
    const [paymentLink, setPaymentLink] = useState(null); // { bookingId, shortUrl, amount }
    const [copied, setCopied] = useState(false);

    /* ---- Loads ---- */

    useEffect(() => {
        if (!preselectedCustomerId) return;
        (async () => {
            const response = await Handler({ method: "get", url: `/admin/customer/${preselectedCustomerId}` });
            if (response?.success && response?.data?.data) setCustomer(response.data.data);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        (async () => {
            const response = await Handler({ method: "get", url: "/admin/booking/protection-tiers" });
            if (response?.success) {
                // Highest percentage first; it is preselected below.
                const tiers = [...(response?.data?.data || [])].sort((a, b) => Number(b.percentage) - Number(a.percentage));
                setProtectionTiers(tiers);
                if (tiers.length) setProtectionId((prev) => prev || tiers[0].tpId);
            }
            setProtectionLoaded(true);
        })();
    }, []);

    useEffect(() => {
        (async () => {
            const response = await Handler({ method: "get", url: "/admin/booking/lead-time" });
            const hours = Number(response?.data?.data?.hours);
            if (!response?.success || !Number.isFinite(hours)) return;
            const real = createDateRules(rulesFromSettings({ leadTime: hours, duration: response?.data?.data?.duration }, "admin"));
            setDates(real);
            const current = rangeRef.current;
            const fitted = dateTouchedRef.current
                ? { ...real.clampRange(current), adjusted: false }
                : initialRange(real, urlDates.start, urlDates.end);
            if (fitted.start.getTime() !== current.start.getTime() || fitted.end.getTime() !== current.end.getTime()) {
                setRange({ start: fitted.start, end: fitted.end });
            }
            setAdjustedNote(!dateTouchedRef.current && (urlDates.start || urlDates.end) && fitted.adjusted ? fitted : null);
        })();
        // Once on mount: urlDates is a read-once snapshot by design.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        (async () => {
            const response = await Handler({ method: "get", url: "/admin/booking/delivery-limits" });
            const maxKm = Number(response?.data?.data?.maxKm);
            if (response?.success && Number.isFinite(maxKm)) setMaxDeliveryKm(maxKm);
        })();
    }, []);

    useEffect(() => {
        if (customer) return undefined;
        if (customerDebounce.current) clearTimeout(customerDebounce.current);
        if (!customerQuery.trim()) { setCustomerResults([]); setCustomerSearching(false); return undefined; }
        setCustomerSearching(true);
        customerDebounce.current = setTimeout(async () => {
            const response = await Handler({
                method: "post",
                url: "/admin/customer/list/0",
                params: { count: 8 },
                data: { search: customerQuery.trim(), status: true },
            });
            setCustomerResults(response?.success ? (response?.data?.data?.list || []) : []);
            setCustomerActive(0);
            setCustomerSearching(false);
        }, 300);
        return () => clearTimeout(customerDebounce.current);
    }, [customerQuery, customer]);

    // Always fetched, so "Change car" can switch back to the list instantly.
    useEffect(() => {
        (async () => {
            setCarsLoading(true);
            const response = await Handler({ method: "get", url: "/admin/car/active" });
            setActiveCars(response?.success ? (response?.data?.data || []) : []);
            setCarsLoading(false);
        })();
    }, []);

    useEffect(() => {
        if (!car?.carId) { setCarDetail(null); setPackageKey(""); return; }
        (async () => {
            setCarDetailLoading(true);
            const response = await Handler({ method: "get", url: `/admin/car/details/${car.carId}` });
            if (response?.success && response?.data?.data) {
                setCarDetail(response.data.data);
                // Always the new car's own first package - packages are per car.
                setPackageKey(String(response.data.data?.priceTypes?.[0]?.perHourKm ?? ""));
            } else {
                setCarDetail(null);
                toast.error(response?.data?.message || "Couldn't load this car's pricing");
            }
            setCarDetailLoading(false);
        })();
    }, [car]);
    useEffect(() => { setActiveImage(0); }, [carDetail?.carId]);

    /* ---- Derived state ---- */

    const tripHours = endDate > startDate ? (new Date(endDate) - new Date(startDate)) / 36e5 : 0;
    const filteredCars = carQuery.trim()
        ? activeCars.filter((c) => c.registrationNo?.toLowerCase().includes(carQuery.trim().toLowerCase()))
        : activeCars;
    const radiusText = maxDeliveryKm ? ` (${maxDeliveryKm} km)` : "";
    // Same address rule as the API (5-300 characters) so staff get a usable address.
    const addressIssue = (address, label) => {
        const msg = textError(address, { label: `${label} address`, required: true, min: RULES.ADDRESS_MIN, max: RULES.ADDRESS_MAX });
        return msg ? `${msg}.` : null;
    };
    const deliveryIssue = deliveryMode !== "deliver" ? null
        : !deliveryDistanceKm ? "Pick the delivery address on the map."
        : !deliveryWithinLimit ? `Delivery address is outside the deliverable radius${radiusText}.`
        : addressIssue(deliveryAddress, "Delivery");
    const pickupIssue = pickupMode !== "collect" ? null
        : !pickupDistanceKm ? "Pick the collection address on the map."
        : !pickupWithinLimit ? `Collection address is outside the deliverable radius${radiusText}.`
        : addressIssue(pickupAddress, "Collection");
    const noProtection = protectionLoaded && protectionTiers.length === 0;
    const selectedTier = protectionTiers.find((t) => t.tpId === protectionId) || null;
    const protectionPending = !protectionLoaded || (protectionTiers.length > 0 && !protectionId);

    const blockers = [
        !customer && { id: "rg-customer", text: "Choose a customer" },
        !carDetail && { id: "rg-car", text: car && carDetailLoading ? "Loading car details" : car ? "This car couldn't load. Choose another" : "Choose a car" },
        !(tripHours > 0) && { id: "rg-trip", text: "Set pickup and drop-off" },
        carDetail && !packageKey && { id: "rg-package", text: "Choose a package" },
        deliveryIssue && { id: "rg-handover", text: deliveryIssue.replace(/\.$/, "") },
        pickupIssue && { id: "rg-handover", text: pickupIssue.replace(/\.$/, "") },
        noProtection && { id: "rg-protection", text: "Enable a protection tier" },
    ].filter(Boolean);

    // The exact body the quote endpoint prices. Its JSON is the freshness key:
    // a quote only counts while it matches what is on screen.
    const quoteBody = useMemo(() => {
        if (!customer || !car || !carDetail || !packageKey || !(endDate > startDate) || deliveryIssue || pickupIssue || noProtection || protectionPending) return null;
        return {
            carId: car.carId,
            customerId: customer.customerId,
            startDate: new Date(startDate).toISOString(),
            endDate: new Date(endDate).toISOString(),
            packageKey,
            ...(protectionId ? { protectionId } : {}),
            ...(couponCode ? { couponCode } : {}),
            ...(useCoins ? { useCoins: true } : {}),
            ...(liveOverride && liveOverride.customerId === customer.customerId ? { override: { limit: "live_bookings", reason: liveOverride.reason } } : {}),
            // Distances are the picker's estimate, sent as a fallback - lat/lng
            // (+hubId) are what BookingUtils.quote re-measures when present.
            ...(deliveryMode === "deliver" ? {
                deliveryDistance: Number(deliveryDistanceKm),
                ...(carDetail?.hub && deliveryLat && deliveryLng ? { deliveryHubId: carDetail.hub, deliveryLat: Number(deliveryLat), deliveryLng: Number(deliveryLng) } : {}),
            } : {}),
            ...(pickupMode === "collect" ? {
                pickupDistance: Number(pickupDistanceKm),
                ...(carDetail?.hub && pickupLat && pickupLng ? { pickupHubId: carDetail.hub, pickupLat: Number(pickupLat), pickupLng: Number(pickupLng) } : {}),
            } : {}),
        };
    }, [liveOverride, customer, car, carDetail, packageKey, startDate, endDate, deliveryIssue, pickupIssue, noProtection, protectionPending, protectionId, couponCode, useCoins,
        deliveryMode, deliveryDistanceKm, deliveryLat, deliveryLng, pickupMode, pickupDistanceKm, pickupLat, pickupLng]);
    const quoteKey = quoteBody ? JSON.stringify(quoteBody) : "";

    // Pricing runs only when the admin presses Get quote. A quote belongs to the
    // exact inputs it was priced for (quoteKey): change anything and it goes
    // stale until it is fetched again.
    const getQuote = async () => {
        if (blockers.length) { setShowErrors(true); scrollToSection(blockers[0].id); return; }
        if (!quoteKey || quoting) return;
        const key = quoteKey;
        const request = ++quoteRequestRef.current;
        setQuoting(true);
        const response = await Handler({ method: "post", url: "/admin/booking/quote", data: JSON.parse(key) });
        if (request !== quoteRequestRef.current) return; // superseded
        if (response?.success) {
            setQuote({ key, data: response.data.data });
            setQuoteError(null);
        } else {
            const body = response?.data || {};
            setQuoteError({ key, message: body.message || "Couldn't price this booking", code: body.code || null, overridable: body.overridable === true });
        }
        setQuoting(false);
    };

    // After the waiver is saved the quote body has changed; price again with it.
    useEffect(() => {
        if (requote && quoteBody?.override) { setRequote(false); getQuote(); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requote, quoteKey]);

    const allowLiveLimit = () => {
        const text = overrideReason.trim();
        if (text.length < 5 || text.length > 200) { toast.error("Write a reason of 5 to 200 characters"); return; }
        setLiveOverride({ customerId: customer.customerId, reason: text });
        setOverrideReason("");
        setRequote(true);
    };
    const overrideBox = quoteError?.overridable && quoteError.code === "customer_live_limit" ? (
        <div className="rg-field">
            <label htmlFor="rg-override-reason">Allow this booking anyway? Write the reason (kept on the booking)</label>
            <input id="rg-override-reason" className="rg-input" maxLength={200} value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
            <button type="button" className="rg-btn" onClick={allowLiveLimit} disabled={quoting || overrideReason.trim().length < 5}>Allow and re-quote</button>
        </div>
    ) : null;

    const q = quote?.data || null;
    const quoteFresh = !!quote && !!quoteKey && quote.key === quoteKey;
    const quoteStale = !!quote && !quoteFresh;
    const quoteFailed = !!quoteError && quoteError.key === quoteKey && !quoting;
    const couponMessage = quoteFresh && couponCode ? q?.couponMessage : null;

    const steps = [
        { id: "rg-customer", label: "Customer", done: !!customer },
        { id: "rg-car", label: "Car", done: !!carDetail },
        { id: "rg-trip", label: "Dates", done: tripHours > 0 && !!carDetail },
        { id: "rg-package", label: "Package", done: !!carDetail && !!packageKey },
        { id: "rg-handover", label: "Handover", done: !!carDetail && !deliveryIssue && !pickupIssue },
        { id: "rg-protection", label: "Protection", done: !!carDetail && !noProtection },
        { id: "rg-extras", label: "Discounts", optional: true, done: (!!couponCode && !couponMessage) || useCoins },
    ];

    /* ---- Actions ---- */

    const pickCustomer = (c) => { setCustomer(c); setCustomerResults([]); setCustomerQuery(""); };
    const onCustomerKey = (e) => {
        if (!customerResults.length) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setCustomerActive((i) => (i + 1) % customerResults.length); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setCustomerActive((i) => (i - 1 + customerResults.length) % customerResults.length); }
        else if (e.key === "Enter") { e.preventDefault(); pickCustomer(customerResults[customerActive] || customerResults[0]); }
        else if (e.key === "Escape") { setCustomerResults([]); }
    };
    const changeCustomer = () => {
        setCustomer(null); setUseCoins(false); setWhatsappConsent(false);
        setTimeout(() => customerInputRef.current?.focus(), 0);
    };
    const changeCar = () => { setCar(null); setCarDetail(null); setCarQuery(""); };
    const applyCoupon = () => {
        const code = couponInput.trim().toUpperCase();
        const msg = textError(code, { label: "Coupon code", required: true, pattern: RULES.COUPON_RE, patternMessage: "Coupon codes use 3 to 30 letters, numbers, - or _" });
        if (msg) { setCouponError(msg); return; }
        setCouponError(null);
        setCouponCode(code);
    };
    const removeCoupon = () => { setCouponCode(""); setCouponInput(""); };
    const findFreeCar = () => navigate(`/booking/availability?s=${new Date(startDate).getTime()}&e=${new Date(endDate).getTime()}`);

    const registerAndGenerateLink = async () => {
        if (blockers.length) { setShowErrors(true); scrollToSection(blockers[0].id); return; }
        if (!quoteFresh) return; // only a quote for exactly what is on screen can be registered
        setRegistering(true);
        const prebookResponse = await Handler({
            method: "post",
            url: "/admin/booking/prebook",
            data: {
                customerId: customer.customerId,
                carId: car.carId,
                startDate: new Date(startDate).toISOString(),
                endDate: new Date(endDate).toISOString(),
                packageKey,
                ...(protectionId ? { protectionId } : {}),
                ...(couponCode ? { couponCode } : {}),
                ...(useCoins ? { useCoins: true } : {}),
                ...(quoteBody?.override ? { override: quoteBody.override } : {}),
                ...(whatsappConsent ? { whatsappConsent: true } : {}),
                ...(deliveryMode === "deliver" ? { deliveryDetails: { distanceKm: Number(deliveryDistanceKm), address: deliveryAddress.trim(), hubId: carDetail?.hub, ...(deliveryLat && deliveryLng ? { lat: Number(deliveryLat), lng: Number(deliveryLng) } : {}) } } : {}),
                ...(pickupMode === "collect" ? { pickupDetails: { distanceKm: Number(pickupDistanceKm), address: pickupAddress.trim(), hubId: carDetail?.hub, ...(pickupLat && pickupLng ? { lat: Number(pickupLat), lng: Number(pickupLng) } : {}) } } : {}),
            },
        });
        if (!prebookResponse?.success) {
            toast.error(prebookResponse?.data?.message || "Couldn't register this booking");
            if (prebookResponse?.data?.code === "customer_live_limit" && prebookResponse?.data?.overridable) setLiveOverride(null);
            setRegistering(false);
            setQuote(null); // the failure may be a price or availability change: quote again
            return;
        }
        const bookingId = prebookResponse.data.data.bookingId;
        // Test mode: a test-mode customer's booking is paid on the Razorpay test account.
        const paymentMode = prebookResponse.data.data.paymentMode === "test" ? "test" : "live";
        const linkResponse = await Handler({ method: "post", url: `/admin/booking/${bookingId}/payment-link` });
        setRegistering(false);
        if (linkResponse?.success) {
            setPaymentLink({ bookingId, paymentMode, ...linkResponse.data.data });
            toast.success("Booking registered. Payment link ready.");
        } else {
            toast.error(linkResponse?.data?.message || `Booking ${bookingId} registered, but the payment link couldn't be created. Retry from here.`);
            setPaymentLink({ bookingId, paymentMode, shortUrl: null, amount: prebookResponse.data.data.amount });
        }
        window.scrollTo({ top: 0 });
    };

    const retryLink = async () => {
        if (!paymentLink?.bookingId) return;
        setRegistering(true);
        const linkResponse = await Handler({ method: "post", url: `/admin/booking/${paymentLink.bookingId}/payment-link` });
        setRegistering(false);
        if (linkResponse?.success) setPaymentLink((prev) => ({ ...prev, ...linkResponse.data.data }));
        else toast.error(linkResponse?.data?.message || "Still couldn't create the payment link");
    };

    const copyLink = async () => {
        if (!paymentLink?.shortUrl) return;
        try {
            await navigator.clipboard.writeText(paymentLink.shortUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Couldn't copy automatically. Select the link and copy it manually.");
        }
    };

    const startOver = () => {
        setCustomer(null); setCustomerQuery(""); setCustomerResults([]); setWhatsappConsent(false);
        setCar(null); setCarDetail(null); setCarQuery("");
        const fresh = initialRange(dates, null, null);
        setRange({ start: fresh.start, end: fresh.end });
        dateTouchedRef.current = false; setAdjustedNote(null);
        setPackageKey(""); setProtectionId(protectionTiers[0]?.tpId || ""); setCouponInput(""); setCouponCode(""); setUseCoins(false);
        setDeliveryMode("self"); setDeliveryAddress(""); setDeliveryDistanceKm(""); setDeliveryLat(""); setDeliveryLng(""); setDeliveryWithinLimit(true);
        setPickupMode("self"); setPickupAddress(""); setPickupDistanceKm(""); setPickupLat(""); setPickupLng(""); setPickupWithinLimit(true);
        setQuote(null); setQuoteError(null); setShowErrors(false); setPaymentLink(null); setPayState("waiting");
        window.scrollTo({ top: 0 });
    };

    /* ---- Live payment status (success screen) ---- */

    // Events only nudge; the REST payment ledger is the source of truth.
    const syncPayState = async () => {
        if (!paymentLink?.bookingId) return;
        const response = await Handler({ method: "get", url: `/admin/booking/${paymentLink.bookingId}/payment` });
        if (!response?.success) return;
        const rows = response?.data?.data?.payments || [];
        if (rows.some((row) => row.status === "captured" || row.status === "partially_refunded" || row.status === "refunded")) {
            setPayState((prev) => {
                if (prev !== "paid") toast.success(`Payment received for ${paymentLink.bookingId}`);
                return "paid";
            });
        }
    };
    const { connected: payLive } = usePaymentEvents({
        bookingId: paymentLink?.bookingId,
        onEvent: (event) => {
            if (event.linkId && paymentLink?.linkId && event.linkId !== paymentLink.linkId && event.kind === "link") return;
            if ((event.kind === "payment" && event.status === "captured") || (event.kind === "link" && event.status === "paid")) {
                setPayState((prev) => {
                    if (prev !== "paid") toast.success(`Payment received for ${paymentLink?.bookingId}`);
                    return "paid";
                });
            } else if (event.kind === "link" && (event.status === "expired" || event.status === "cancelled")) {
                setPayState((prev) => (prev === "paid" ? prev : "expired"));
            } else if (event.kind === "payment" && event.status === "failed") {
                setPayState((prev) => (prev === "paid" ? prev : "failed"));
            }
            syncPayState();
        },
        onResync: syncPayState,
    });

    /* ---- Success ---- */

    if (paymentLink) {
        const linkOk = !!paymentLink.shortUrl;
        return (
            <div className="rg rg-done">
                <div className="rg-done-head">
                    <span className="rg-done-mark" aria-hidden="true"><BiCheck /></span>
                    <div>
                        <p className="rg-eyebrow">Booking registered</p>
                        <h1 className="rg-title">{paymentLink.bookingId}<TestModeTag mode={paymentLink.paymentMode} className="ms-2 fs-6 align-middle" /></h1>
                        <p className="rg-sub">
                            {payState === "paid"
                                ? <>Payment of <strong>{currencyFormatter(paymentLink.amount || q?.total || 0)}</strong> received from {fullName(customer)}.</>
                                : <>Awaiting payment of <strong>{currencyFormatter(paymentLink.amount || q?.total || 0)}</strong> from {fullName(customer)}.</>}
                            {" "}<LiveBadge connected={payLive} />
                        </p>
                        {paymentLink.paymentMode === "test" ? (
                            <p className="rg-sub text-warning-emphasis">Test mode: this link uses the Razorpay test account. Pay with a test card or test UPI; no real money moves.</p>
                        ) : null}
                    </div>
                </div>

                <div className="rg-done-grid">
                    <div className="rg-done-main">
                        <ol className="rg-timeline">
                            <li className="is-done"><span><BiCheck /></span><div><strong>Booking saved</strong><p>The car is held for these dates.</p></div></li>
                            <li className={linkOk ? "is-done" : "is-error"}>
                                <span>{linkOk ? <BiCheck /> : <BiX />}</span>
                                <div>
                                    <strong>{linkOk ? "Payment link created" : "Payment link failed"}</strong>
                                    <p>{linkOk ? "It is emailed to the customer, and sent on WhatsApp when that is configured." : "The booking is saved. Retry to create the link."}</p>
                                </div>
                            </li>
                            {payState === "paid" ? (
                                <>
                                    <li className="is-done"><span><BiCheck /></span><div><strong>Payment received</strong><p>The customer paid through the link.</p></div></li>
                                    <li className="is-done"><span><BiCheck /></span><div><strong>Booking confirmed</strong><p>The car is now booked for these dates.</p></div></li>
                                </>
                            ) : payState === "expired" ? (
                                <li className="is-error"><span><BiX /></span><div><strong>Payment link expired</strong><p>Open the booking to send a fresh link.</p></div></li>
                            ) : (
                                <li className="is-pending">
                                    <span><BiTimeFive /></span>
                                    <div>
                                        <strong>{payState === "failed" ? "Last attempt failed, still awaiting payment" : "Awaiting payment"}</strong>
                                        <p>{payState === "failed" ? "The customer can retry with the same link." : "The booking confirms when the customer pays."}</p>
                                    </div>
                                </li>
                            )}
                        </ol>

                        {linkOk ? (
                            <div className="rg-link">
                                <label htmlFor="rg-link-url">Payment link</label>
                                <div className="rg-link-row">
                                    <input id="rg-link-url" type="text" value={paymentLink.shortUrl} readOnly onFocus={(e) => e.target.select()} />
                                    <button type="button" className="rg-btn rg-btn--ghost" onClick={copyLink}>{copied ? <><BiCheck /> Copied</> : <><BiCopy /> Copy</>}</button>
                                    <a href={paymentLink.shortUrl} target="_blank" rel="noopener noreferrer" className="rg-btn rg-btn--ghost"><BiLinkExternal /> Open</a>
                                </div>
                            </div>
                        ) : (
                            <div className="rg-callout rg-callout--danger">
                                <BiErrorCircle aria-hidden="true" />
                                <span>The payment link couldn&apos;t be created.</span>
                                <button type="button" className="rg-btn rg-btn--danger" onClick={retryLink} disabled={registering}>
                                    {registering ? <><BiLoaderAlt className="rg-spin" /> Retrying</> : <><BiRefresh /> Retry</>}
                                </button>
                            </div>
                        )}

                        <div className="rg-done-actions">
                            <button type="button" className="rg-btn rg-btn--primary" onClick={startOver}>Register another booking</button>
                            <button type="button" className="rg-btn rg-btn--ghost" onClick={() => navigate(`/booking/details/${paymentLink.bookingId}`)}>View booking</button>
                            <button type="button" className="rg-btn rg-btn--text" onClick={() => navigate(listBackPath("/booking"))}>Back to bookings</button>
                        </div>
                    </div>

                    <dl className="rg-receipt">
                        <LedgerRow label="Customer" value={fullName(customer)} />
                        <LedgerRow label="Phone" value={customer?.phone || "-"} />
                        <LedgerRow label="Car" value={`${carName(carDetail)} · ${carDetail?.registrationNo || ""}`} />
                        <LedgerRow label="Pickup" value={formatRangeDate(startDate)} />
                        <LedgerRow label="Drop-off" value={formatRangeDate(endDate)} />
                        <LedgerRow label="Duration" value={formatDuration(tripHours)} />
                        {q && <LedgerRow label="Payable now" value={currencyFormatter(q.payableNow ?? 0)} />}
                        {q && <LedgerRow label="Refundable deposit" value={currencyFormatter(q.securityDeposit?.net ?? 0)} />}
                    </dl>
                </div>
            </div>
        );
    }

    /* ---- Quote rail (shared by desktop rail and mobile sheet) ---- */

    const ctaMode = quoteFresh ? "register" : "quote";
    const onCta = ctaMode === "register" ? registerAndGenerateLink : getQuote;
    const ctaLabel = registering ? "Registering" : quoting ? "Getting quote" : ctaMode === "register" ? `Register · ${currencyFormatter(q?.total ?? 0)}` : quoteStale ? "Update quote" : "Get quote";
    const ctaBusy = registering || quoting;
    const ctaDisabled = ctaBusy;
    const status = blockers.length ? { tone: "idle", text: `${blockers.length} to go` }
        : quoting ? { tone: "busy", text: "Pricing" }
        : quoteFailed ? { tone: "error", text: "Error" }
        : quoteFresh ? { tone: "live", text: "Quoted" }
        : quoteStale ? { tone: "stale", text: "Outdated" } : { tone: "idle", text: "Ready" };

    const rail = (
        <div className="rg-rail-inner">
            <div className="rg-rail-head">
                <h2>Quote</h2>
                <span className={`rg-pill rg-pill--${status.tone}`}>
                    {status.tone === "busy" && <BiLoaderAlt className="rg-spin" aria-hidden="true" />}{status.text}
                </span>
            </div>

            <div className="rg-summary">
                <div className="rg-summary-car">
                    <div className="rg-thumb">{carDetail?.carImages?.[0] ? <img src={carDetail.carImages[0]} alt="" /> : <CarGlyph />}</div>
                    <div className="rg-min">
                        <strong>{carDetail ? carName(carDetail) : "No car yet"}</strong>
                        <span className="rg-mono">{carDetail?.registrationNo || "-"}</span>
                    </div>
                </div>
                <dl className="rg-summary-list">
                    <LedgerRow label="Customer" value={customer ? fullName(customer) : "-"} />
                    <LedgerRow label="Pickup" value={formatRangeDate(startDate)} />
                    <LedgerRow label="Drop-off" value={formatRangeDate(endDate)} />
                    <LedgerRow label="Duration" value={tripHours > 0 ? formatDuration(tripHours) : "-"} />
                    {selectedTier && <LedgerRow label="Damage fine" value={currencyFormatter(selectedTier.amount ?? 0)} />}
                </dl>
            </div>

            {blockers.length > 0 ? (
                <div className="rg-todo">
                    <p>To price this booking:</p>
                    <ul>
                        {blockers.map((b) => (
                            <li key={b.text}><button type="button" onClick={() => scrollToSection(b.id)}>{b.text}<BiRightArrowAlt aria-hidden="true" /></button></li>
                        ))}
                    </ul>
                </div>
            ) : quoteFailed && !q ? (
                <div className="rg-callout rg-callout--danger">
                    <BiErrorCircle aria-hidden="true" /><span>{quoteError.message}</span>
                    <button type="button" className="rg-btn rg-btn--text" onClick={getQuote}><BiRefresh /> Retry</button>
                    {overrideBox}
                </div>
            ) : q ? (
                <div className={`rg-ledger${quoteFresh ? "" : " is-stale"}`} aria-live="polite" aria-busy={quoting}>
                    {quoteFailed && (
                        <div className="rg-callout rg-callout--danger">
                            <BiErrorCircle aria-hidden="true" /><span>{quoteError.message}. Showing the last price.</span>
                            <button type="button" className="rg-btn rg-btn--text" onClick={getQuote}><BiRefresh /> Retry</button>
                            {overrideBox}
                        </div>
                    )}
                    {quoteStale && !quoting && !quoteFailed && (
                        <div className="rg-callout rg-callout--warn">
                            <BiInfoCircle aria-hidden="true" /><span>Details changed since this quote. Get a new quote before registering.</span>
                        </div>
                    )}
                    <p className="rg-ledger-label">Charged now</p>
                    <dl>
                        <LedgerRow
                            label="Rent"
                            detail={`${currencyFormatter(q.rental?.rate ?? 0)}/hr × ${q.hours} hrs${q.peakPercentage ? ` · peak +${q.peakPercentage}%` : ""}`}
                            value={currencyFormatter(q.rental?.gross ?? q.rental?.net ?? 0)}
                        />
                        {!!q.rental?.discount && <LedgerRow label="Rent discount" value={`−${currencyFormatter(q.rental.discount)}`} tone="credit" />}
                        <LedgerRow label="Trip protection" detail={q.protection?.snapshot ? `Insurance + ${q.protection.snapshot.feePercentage}%` : null} value={currencyFormatter(q.protection?.fee ?? 0)} />
                        {!!q.delivery?.net && <LedgerRow label="Delivery" detail={`${q.delivery.distance} km · ${q.delivery.bands} band${q.delivery.bands !== 1 ? "s" : ""}`} value={currencyFormatter(q.delivery.net)} />}
                        {!!q.pickup?.net && <LedgerRow label="Collection" detail={`${q.pickup.distance} km · ${q.pickup.bands} band${q.pickup.bands !== 1 ? "s" : ""}`} value={currencyFormatter(q.pickup.net)} />}
                        <LedgerRow label="Convenience fee" value={currencyFormatter(q.convenienceFee ?? 0)} />
                        <LedgerRow label="Platform fee" value={currencyFormatter(q.platformFee ?? 0)} />
                        {!!q.coupon?.totalDiscount && <LedgerRow label="Coupon" detail={q.coupon.couponCode} value={`−${currencyFormatter(q.coupon.totalDiscount)}`} tone="credit" />}
                        {!!q.coins?.spent && <LedgerRow label="Coins" value={`−${currencyFormatter(q.coins.spent)}`} tone="credit" />}
                    </dl>
                    <div className="rg-subtotal"><span>Payable now</span><strong>{currencyFormatter(q.payableNow ?? 0)}</strong></div>

                    <p className="rg-ledger-label">Held now, refundable</p>
                    <dl>
                        <LedgerRow
                            label="Security deposit"
                            detail={q.securityDeposit?.discount ? `after ${currencyFormatter(q.securityDeposit.discount)} off` : null}
                            value={currencyFormatter(q.securityDeposit?.net ?? 0)}
                        />
                    </dl>
                    <div className="rg-total">
                        <span>Total charged now</span>
                        <strong>{currencyFormatter(q.total ?? 0)}</strong>
                    </div>
                </div>
            ) : quoting ? (
                <div className="rg-ledger rg-ledger--skeleton" aria-hidden="true">
                    {[70, 55, 62, 48, 58].map((w) => <span key={w} style={{ width: `${w}%` }} />)}
                </div>
            ) : (
                <p className="rg-help rg-ready">Everything is filled in. Press Get quote to see the full price.</p>
            )}

            <button type="button" className="rg-btn rg-btn--primary rg-btn--block" onClick={onCta} disabled={ctaDisabled}>
                {ctaBusy && <BiLoaderAlt className="rg-spin" aria-hidden="true" />}{ctaLabel}
            </button>
            <p className="rg-fine">{ctaMode === "register" ? "Creates the booking and a payment link for the customer. Price and availability are checked again on submit." : "Prices this booking with the details above. Nothing is booked yet."}</p>
        </div>
    );

    /* ---- Form ---- */

    const hubText = [carDetail?.hubDetail?.buildingName, carDetail?.hubDetail?.area].filter(Boolean).join(", ");
    const images = carDetail?.carImages || [];

    return (
        <div className="rg">
            <div className="rg-top">
                <BackButton className="rg-back" />
                <div className="rg-top-text">
                    <h1 className="rg-title">Register a booking</h1>
                    <p className="rg-sub">Priced the same way as the customer app. Payment is collected through a shareable link.</p>
                </div>
            </div>

            <nav className="rg-steps" aria-label="Booking steps">
                <ol>
                    {steps.map((s, i) => (
                        <li key={s.id}>
                            <button type="button" className={`${s.done ? "is-done" : ""}${s.optional ? " is-optional" : ""}`} onClick={() => scrollToSection(s.id)}>
                                <span className="rg-step-dot" aria-hidden="true">{s.done ? <BiCheck /> : i + 1}</span>
                                {s.label}{s.optional && !s.done && <em>optional</em>}
                            </button>
                        </li>
                    ))}
                </ol>
            </nav>

            <div className="rg-layout">
                <div className="rg-form">
                    {/* 1. Customer */}
                    <Section id="rg-customer" index={1} title="Customer" hint="Search by name, phone or email." done={!!customer}
                        error={showErrors && !customer ? "Choose the customer this booking is for." : null}>
                        {customer ? (
                            <>
                            <div className="rg-identity">
                                <span className="rg-avatar" aria-hidden="true">{initialsOf(customer)}</span>
                                <div className="rg-min">
                                    <strong>{fullName(customer)}</strong>
                                    <div className="rg-meta">
                                        {customer.phone && <span><BiPhone aria-hidden="true" />{customer.phone}</span>}
                                        {customer.email?.emailId && <span><BiEnvelope aria-hidden="true" />{customer.email.emailId}</span>}
                                        {typeof customer.coins === "number" && <span><BiCoinStack aria-hidden="true" />{customer.coins} coins</span>}
                                    </div>
                                </div>
                                <button type="button" className="rg-btn rg-btn--text" onClick={changeCustomer}>Change</button>
                            </div>
                            <label className="rg-consent">
                                <input type="checkbox" checked={whatsappConsent} onChange={(e) => setWhatsappConsent(e.target.checked)} />
                                <span>Customer has agreed to get booking updates and reminders on WhatsApp</span>
                            </label>
                            </>
                        ) : (
                            <div className="rg-combo">
                                <div className="rg-input rg-input--icon">
                                    <BiSearch aria-hidden="true" />
                                    <input
                                        ref={customerInputRef}
                                        type="search"
                                        placeholder="e.g. Priya or 98400 12345"
                                        value={customerQuery}
                                        onChange={(e) => setCustomerQuery(e.target.value)}
                                        onKeyDown={onCustomerKey}
                                        aria-label="Search customers"
                                        role="combobox"
                                        aria-expanded={customerResults.length > 0}
                                        aria-controls="rg-customer-list"
                                        autoComplete="off"
                                    />
                                    {customerSearching && <BiLoaderAlt className="rg-spin rg-input-end" aria-hidden="true" />}
                                </div>
                                {!customerSearching && customerQuery.trim() && customerResults.length === 0 && (
                                    <p className="rg-help">No matching customers. Register them first from Customers.</p>
                                )}
                                {customerResults.length > 0 && (
                                    <ul className="rg-options" id="rg-customer-list" role="listbox">
                                        {customerResults.map((c, i) => (
                                            <li key={c.customerId} role="option" aria-selected={i === customerActive}>
                                                <button type="button" className={i === customerActive ? "is-active" : ""} onMouseEnter={() => setCustomerActive(i)} onClick={() => pickCustomer(c)}>
                                                    <span className="rg-avatar rg-avatar--sm" aria-hidden="true">{initialsOf(c)}</span>
                                                    <span className="rg-min"><strong>{fullName(c)}</strong><small>{[c.phone, c.email?.emailId].filter(Boolean).join(" · ")}</small></span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </Section>

                    {/* 2. Car */}
                    <Section id="rg-car" index={2} title="Car" hint={carDetail ? "Details and photos of the car being booked." : "Search the active fleet by registration."}
                        done={!!carDetail} error={showErrors && !carDetail && !carDetailLoading ? "Choose a car to book." : null}>
                        {car ? (
                            carDetailLoading ? (
                                <div className="rg-carview rg-carview--loading" aria-busy="true"><span /><span /></div>
                            ) : carDetail ? (
                                <div className="rg-carview">
                                    <div className="rg-gallery">
                                        <div className="rg-gallery-main">
                                            {images[activeImage] ? <img src={images[activeImage]} alt={`${carName(carDetail)} (${activeImage + 1} of ${images.length})`} /> : <CarGlyph />}
                                            {!!carDetail.star && <span className="rg-rating"><BiSolidStar aria-hidden="true" />{carDetail.star}</span>}
                                        </div>
                                        {images.length > 1 && (
                                            <div className="rg-gallery-thumbs">
                                                {images.map((img, i) => (
                                                    <button type="button" key={img + i} className={i === activeImage ? "is-on" : ""} onClick={() => setActiveImage(i)} aria-label={`Show photo ${i + 1}`}>
                                                        <img src={img} alt="" />
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className="rg-specs">
                                        <div className="rg-specs-head">
                                            <div className="rg-min">
                                                <h3>{carName(carDetail)}</h3>
                                                <span className="rg-plate">{carDetail.registrationNo}</span>
                                            </div>
                                            <button type="button" className="rg-btn rg-btn--text" onClick={changeCar}>Change</button>
                                        </div>
                                        <dl className="rg-specgrid">
                                            {carDetail.year && <div><dt>Year</dt><dd>{carDetail.year}</dd></div>}
                                            {carDetail.transmissionType && <div><dt>Gearbox</dt><dd>{textCapitalize(carDetail.transmissionType)}</dd></div>}
                                            {!!carDetail.fuelType?.length && <div><dt>Fuel</dt><dd>{carDetail.fuelType.map(textCapitalize).join(" / ")}</dd></div>}
                                            {!!carDetail.model?.seats && <div><dt>Seats</dt><dd>{carDetail.model.seats}</dd></div>}
                                            {carDetail.color && <div><dt>Colour</dt><dd>{textCapitalize(carDetail.color)}</dd></div>}
                                            {!!carDetail.odometer?.current && <div><dt>Odometer</dt><dd>{carDetail.odometer.current.toLocaleString("en-IN")} km</dd></div>}
                                        </dl>
                                        {hubText && <p className="rg-hub"><BiStore aria-hidden="true" />{hubText}</p>}
                                    </div>
                                </div>
                            ) : (
                                <div className="rg-callout rg-callout--danger">
                                    <BiErrorCircle aria-hidden="true" /><span>Couldn&apos;t load this car.</span>
                                    <button type="button" className="rg-btn rg-btn--text" onClick={changeCar}>Choose another</button>
                                </div>
                            )
                        ) : (
                            <div className="rg-picker">
                                <div className="rg-picker-bar">
                                    <div className="rg-input rg-input--icon">
                                        <BiSearch aria-hidden="true" />
                                        <input type="search" placeholder="e.g. TN 09 AB 1234" value={carQuery} onChange={(e) => setCarQuery(e.target.value)} aria-label="Search cars by registration" />
                                    </div>
                                    <button type="button" className="rg-btn rg-btn--ghost" onClick={findFreeCar}><BiCalendar /> Find a free car</button>
                                </div>
                                {carsLoading ? (
                                    <div className="rg-carlist rg-carlist--loading" aria-busy="true">{[1, 2, 3, 4].map((k) => <span key={k} />)}</div>
                                ) : filteredCars.length === 0 ? (
                                    <p className="rg-help">No active cars match that registration.</p>
                                ) : (
                                    <ul className="rg-carlist">
                                        {filteredCars.map((c) => (
                                            <li key={c.carId}>
                                                <button type="button" onClick={() => setCar(c)}>
                                                    <BiCar aria-hidden="true" />
                                                    <span className="rg-mono">{c.registrationNo}</span>
                                                    <span className="rg-carlist-go">Select<BiRightArrowAlt aria-hidden="true" /></span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <p className="rg-help">{filteredCars.length} of {activeCars.length} active cars. Not sure which is free? Find a free car checks your dates first.</p>
                            </div>
                        )}
                    </Section>

                    {carDetail && (
                        <>
                            {/* 3. Dates */}
                            <Section id="rg-trip" index={3} title="Pickup & drop-off" hint="Times are IST. Dates this car is already booked are greyed out." done={tripHours > 0}>
                                <div className="rg-trip">
                                    <CarBookingRangePicker id="booking-range" carId={car?.carId} dates={dates} value={range} onChange={onRangeChange} />
                                    {tripHours > 0 && (
                                        <div className="rg-duration"><BiTimeFive aria-hidden="true" /><strong>{formatDuration(tripHours)}</strong><span>{Math.round(tripHours)} hrs billed</span></div>
                                    )}
                                </div>
                                {adjustedNote && (
                                    <p className="rg-note">
                                        The requested dates couldn&apos;t be booked as they were, so they moved to the nearest available times: {formatRangeDate(adjustedNote.start)} to {formatRangeDate(adjustedNote.end)} (IST).
                                    </p>
                                )}
                            </Section>

                            {/* 4. Package */}
                            <Section id="rg-package" index={4} title="Package" hint="Kilometre allowance and hourly rate." done={!!packageKey}
                                error={showErrors && !packageKey ? "This car has no packages. Add one in the car's pricing." : null}>
                                {(carDetail.priceTypes || []).length ? (
                                    <div className="rg-tiles" role="radiogroup" aria-label="Package">
                                        {carDetail.priceTypes.map((p) => {
                                            const on = String(p.perHourKm) === packageKey;
                                            return (
                                                <label key={p.perHourKm} className={`rg-tile${on ? " is-on" : ""}`}>
                                                    <input type="radio" name="package" value={p.perHourKm} checked={on} onChange={() => setPackageKey(String(p.perHourKm))} />
                                                    <span className="rg-tile-name">{textCapitalize(p.package)}</span>
                                                    <span className="rg-tile-rate"><strong>{currencyFormatter(p.perHourPrice)}</strong>/hr</span>
                                                    <span className="rg-tile-meta">{p.perHourKm} km per hour</span>
                                                    {tripHours > 0 && <span className="rg-tile-est">About {currencyFormatter(p.perHourPrice * Math.round(tripHours))} rent</span>}
                                                    {on && <BiCheckCircle className="rg-tile-check" aria-hidden="true" />}
                                                </label>
                                            );
                                        })}
                                    </div>
                                ) : <p className="rg-help">This car has no packages yet.</p>}
                            </Section>

                            {/* 5. Handover */}
                            <Section id="rg-handover" index={5} title="Handover" hint="How the car reaches the customer and comes back." done={!deliveryIssue && !pickupIssue}
                                error={showErrors ? (deliveryIssue || pickupIssue) : null}>
                                <div className="rg-split">
                                    <div className="rg-split-col">
                                        <span className="rg-label">Start of trip</span>
                                        <Segmented name="deliveryMode" value={deliveryMode} onChange={setDeliveryMode} options={[
                                            { value: "self", label: "Hub pickup", icon: <BiStore aria-hidden="true" /> },
                                            { value: "deliver", label: "Deliver", icon: <BiMap aria-hidden="true" /> },
                                        ]} />
                                        <p className="rg-help">{deliveryMode === "self" ? (hubText ? `Customer collects at ${hubText}.` : "Customer collects at the hub.") : "Delivered to the customer's address."}</p>
                                    </div>
                                    <div className="rg-split-col">
                                        <span className="rg-label">End of trip</span>
                                        <Segmented name="pickupMode" value={pickupMode} onChange={setPickupMode} options={[
                                            { value: "self", label: "Hub drop-off", icon: <BiStore aria-hidden="true" /> },
                                            { value: "collect", label: "Collect", icon: <BiMap aria-hidden="true" /> },
                                        ]} />
                                        <p className="rg-help">{pickupMode === "self" ? "Customer returns the car to the hub." : "Collected from the customer's address."}</p>
                                    </div>
                                </div>
                                {deliveryMode === "deliver" && (
                                    <div className="rg-mapblock">
                                        <span className="rg-label">Delivery address</span>
                                        <DeliveryLocationPicker
                                            hubLat={carDetail?.hubDetail?.latitude}
                                            hubLng={carDetail?.hubDetail?.longitude}
                                            hubLabel={carDetail?.hubDetail?.buildingName || carDetail?.hubDetail?.area}
                                            latitude={deliveryLat}
                                            longitude={deliveryLng}
                                            address={deliveryAddress}
                                            maxKm={maxDeliveryKm}
                                            onChange={({ lat, lng, address: resolvedAddress, distanceKm, withinLimit }) => {
                                                setDeliveryLat(lat); setDeliveryLng(lng);
                                                if (resolvedAddress) setDeliveryAddress(resolvedAddress);
                                                if (distanceKm !== null && distanceKm !== undefined) setDeliveryDistanceKm(String(distanceKm));
                                                setDeliveryWithinLimit(withinLimit !== false);
                                            }}
                                            manualDistanceKm={deliveryDistanceKm}
                                            onManualChange={({ address: manualAddress, distanceKm: manualKm }) => {
                                                if (manualAddress !== undefined) setDeliveryAddress(manualAddress);
                                                if (manualKm !== undefined) setDeliveryDistanceKm(manualKm);
                                            }}
                                        />
                                        {!!deliveryDistanceKm && !deliveryWithinLimit && <p className="rg-field-error"><BiErrorCircle aria-hidden="true" />Outside the deliverable radius{radiusText}.</p>}
                                    </div>
                                )}
                                {pickupMode === "collect" && (
                                    <div className="rg-mapblock">
                                        <span className="rg-label">Collection address</span>
                                        <DeliveryLocationPicker
                                            hubLat={carDetail?.hubDetail?.latitude}
                                            hubLng={carDetail?.hubDetail?.longitude}
                                            hubLabel={carDetail?.hubDetail?.buildingName || carDetail?.hubDetail?.area}
                                            latitude={pickupLat}
                                            longitude={pickupLng}
                                            address={pickupAddress}
                                            maxKm={maxDeliveryKm}
                                            onChange={({ lat, lng, address: resolvedAddress, distanceKm, withinLimit }) => {
                                                setPickupLat(lat); setPickupLng(lng);
                                                if (resolvedAddress) setPickupAddress(resolvedAddress);
                                                if (distanceKm !== null && distanceKm !== undefined) setPickupDistanceKm(String(distanceKm));
                                                setPickupWithinLimit(withinLimit !== false);
                                            }}
                                            manualDistanceKm={pickupDistanceKm}
                                            onManualChange={({ address: manualAddress, distanceKm: manualKm }) => {
                                                if (manualAddress !== undefined) setPickupAddress(manualAddress);
                                                if (manualKm !== undefined) setPickupDistanceKm(manualKm);
                                            }}
                                        />
                                        {!!pickupDistanceKm && !pickupWithinLimit && <p className="rg-field-error"><BiErrorCircle aria-hidden="true" />Outside the deliverable radius{radiusText}.</p>}
                                    </div>
                                )}
                            </Section>

                            {/* 6. Protection */}
                            <Section id="rg-protection" index={6} title="Trip protection" hint="Fee is the car's insurance for the trip plus the tier's percentage. The damage fine applies if the car is damaged." done={!noProtection}
                                error={noProtection ? "No protection tiers are enabled, so this booking can't be priced." : null}>
                                {noProtection ? (
                                    <button type="button" className="rg-btn rg-btn--ghost" onClick={() => navigate(listBackPath("/protection"))}><BiShieldQuarter /> Set up protection tiers</button>
                                ) : (
                                    <div className="rg-radiolist" role="radiogroup" aria-label="Trip protection">
                                        {protectionTiers.map((tier, i) => (
                                            <label key={tier.tpId} className={`rg-radio${protectionId === tier.tpId ? " is-on" : ""}`}>
                                                <input type="radio" name="protection" checked={protectionId === tier.tpId} onChange={() => setProtectionId(tier.tpId)} />
                                                <span className="rg-radio-dot" aria-hidden="true" />
                                                <span className="rg-min">
                                                    <strong>{stripHtml(tier.description)}{i === 0 && <span className="rg-tag">Highest</span>}</strong>
                                                    <small>Damage fine {currencyFormatter(tier.amount ?? 0)}</small>
                                                </span>
                                                <span className="rg-radio-end">+{tier.percentage}%</span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </Section>

                            {/* 7. Discounts */}
                            <Section id="rg-extras" index={7} title="Discounts" hint="Coupon and reward coins." optional done={(!!couponCode && !couponMessage) || useCoins}
                                error={couponMessage || null}>
                                <div className="rg-extras">
                                    <div className="rg-coupon">
                                        <label className="rg-label" htmlFor="rg-coupon">Coupon code</label>
                                        {couponCode ? (
                                            <div className={`rg-chip${couponMessage ? " rg-chip--bad" : ""}`}>
                                                <BiPurchaseTag aria-hidden="true" /><span className="rg-mono">{couponCode}</span>
                                                {quoteFresh && !couponMessage && !!q?.coupon?.totalDiscount && <em>−{currencyFormatter(q.coupon.totalDiscount)}</em>}
                                                <button type="button" onClick={removeCoupon} aria-label="Remove coupon"><BiX /></button>
                                            </div>
                                        ) : (
                                            <form className="rg-coupon-row" onSubmit={(e) => { e.preventDefault(); applyCoupon(); }}>
                                                <div className="rg-input">
                                                    <input id="rg-coupon" type="text" placeholder="e.g. WEEKEND10" value={couponInput} maxLength={30} aria-invalid={!!couponError} aria-describedby={couponError ? "rg-coupon-err" : undefined} onChange={(e) => { setCouponInput(e.target.value.toUpperCase().replace(/\s/g, "")); setCouponError(null); }} autoComplete="off" />
                                                </div>
                                                <button type="submit" className="rg-btn rg-btn--ghost" disabled={!couponInput.trim()}>Apply</button>
                                                {couponError && <p id="rg-coupon-err" className="rg-field-error" role="alert">{couponError}</p>}
                                            </form>
                                        )}
                                    </div>
                                    <label className={`rg-switch${!customer?.coins ? " is-disabled" : ""}`}>
                                        <input type="checkbox" role="switch" checked={useCoins} onChange={(e) => setUseCoins(e.target.checked)} disabled={!customer?.coins} />
                                        <span className="rg-switch-track" aria-hidden="true"><span /></span>
                                        <span className="rg-min">
                                            <strong>Use reward coins</strong>
                                            <small>{customer ? (customer.coins ? `${customer.coins} coins available` : "This customer has no coins") : "Choose a customer first"}</small>
                                        </span>
                                    </label>
                                </div>
                            </Section>
                        </>
                    )}
                    {!carDetail && (
                        <div className="rg-locked">
                            <BiPackage aria-hidden="true" />
                            <p>Dates, package, handover, protection and discounts open once a car is chosen.</p>
                        </div>
                    )}
                </div>

                <aside className="rg-rail" id="rg-quote" aria-label="Quote">{rail}</aside>
            </div>

            {/* Mobile action bar: total + primary action always in reach. */}
            <div className="rg-mbar">
                <button type="button" className="rg-mbar-sum" onClick={() => scrollToSection("rg-quote")}>
                    <small>{blockers.length ? `${blockers.length} step${blockers.length > 1 ? "s" : ""} to go` : quoteFresh ? "Total now" : quoteStale ? "Quote outdated" : "Not priced yet"}</small>
                    <strong>{quoteFresh ? currencyFormatter(q?.total ?? 0) : "-"}</strong>
                </button>
                <button type="button" className="rg-btn rg-btn--primary" onClick={onCta} disabled={ctaDisabled}>
                    {ctaBusy ? <><BiLoaderAlt className="rg-spin" aria-hidden="true" /> {registering ? "Registering" : "Pricing"}</> : ctaMode === "register" ? "Register" : quoteStale ? "Update quote" : "Get quote"}
                </button>
            </div>
        </div>
    );
};

export default BookingRegister;
